import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

import {
    BatchDetail,
    BatchListItem,
    BatchRun,
    errorMessage,
    Feedback,
    getReplayBatch,
    listReplayBatches,
    PromptSource,
    REPLAY_MODEL_SUGGESTIONS,
    startReplayBatch,
} from '../replaysApi';

import { ReplayDialog } from './ReplayDialog';
import { CheckChips, VerdictControl } from './VerdictControl';
import { GlowAlert } from '../../shared/components/GlowAlert';
import { LoadingButton } from '../../shared/components/LoadingButton';

const POLL_MS = 3000;
const EXCERPT_CHARS = 220;

interface ReplayBatchDialogProps {
    /** The user whose replies to offer replaying; every user's are always available. */
    userId?: string;
    userName?: string;
    onClose: () => void;
}

const excerpt = (text: string | null): string => {
    const value = (text ?? '').trim();
    return value.length > EXCERPT_CHARS ? `${value.slice(0, EXCERPT_CHARS)}…` : value;
};

const Tools: React.FC<{ tools: string[]; blocked?: string[] }> = ({ tools, blocked = [] }) => (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
        {tools.length === 0 && <Chip size="small" variant="outlined" label="no tool call" />}
        {tools.map((name, index) => (
            <Chip
                key={`${name}-${index}`}
                size="small"
                variant={blocked.includes(name) ? 'filled' : 'outlined'}
                color={blocked.includes(name) ? 'warning' : 'default'}
                label={blocked.includes(name) ? `${name} (blocked)` : name}
            />
        ))}
    </Box>
);

const describe = (batch: BatchListItem): string => {
    const who = batch.criteria?.user_id ? 'one user' : 'all users';
    const what = batch.criteria?.feedback ? `${batch.criteria.feedback === 'down' ? '👎' : '👍'} replies, ${who}` : 'chosen replies';
    return `${batch.model} · ${what} · ${batch.done + batch.failed}/${batch.total}`;
};

const Row: React.FC<{ run: BatchRun; onOpen: () => void; onVerdict: () => void }> = ({ run, onOpen, onVerdict }) => (
    <TableRow hover sx={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={onOpen} data-testid="batch-run">
        <TableCell sx={{ width: '22%' }}>
            <Typography variant="body2">{excerpt(run.user_text)}</Typography>
        </TableCell>
        <TableCell sx={{ width: '28%' }}>
            <Tools tools={run.original_tools} />
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {excerpt(run.original_reply)}
            </Typography>
        </TableCell>
        <TableCell sx={{ width: '30%' }}>
            {run.status === 'done' && (
                <>
                    <Tools tools={run.summary.tools} blocked={run.summary.blocked} />
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {excerpt(run.reply)}
                    </Typography>
                </>
            )}
            {run.status === 'running' && <LinearProgress />}
            {run.status === 'failed' && <GlowAlert>{run.error ?? 'Failed'}</GlowAlert>}
        </TableCell>
        <TableCell>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                {run.summary.guard_fired && <Chip size="small" color="info" label="claim guard fired" />}
                {run.duration_ms !== null && (
                    <Typography variant="caption" color="text.secondary">
                        {(run.duration_ms / 1000).toFixed(1)}s
                    </Typography>
                )}
            </Box>
            {run.status === 'done' && (
                <Box sx={{ display: 'grid', gap: 0.5 }}>
                    <CheckChips checks={run.checks} />
                    <VerdictControl run={run} compact onSaved={onVerdict} />
                </Box>
            )}
        </TableCell>
    </TableRow>
);

/**
 * Replays the most recent thumbs-down (or up) replies with one model and lays the results
 * out against what actually happened, to compare models or check a fix. Replays never
 * change anything: tools that write are stubs.
 */
export const ReplayBatchDialog: React.FC<ReplayBatchDialogProps> = ({ userId, userName, onClose }) => {
    const [feedback, setFeedback] = useState<Feedback>('down');
    const [scope, setScope] = useState<'user' | 'all'>(userId ? 'user' : 'all');
    const [limit, setLimit] = useState(10);
    const [model, setModel] = useState('');
    const [promptSource, setPromptSource] = useState<PromptSource>('recorded');
    const [batches, setBatches] = useState<BatchListItem[] | null>(null);
    const [selected, setSelected] = useState<string>('');
    const [detail, setDetail] = useState<BatchDetail | null>(null);
    const [starting, setStarting] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [openTurn, setOpenTurn] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        try {
            const items = await listReplayBatches();
            setBatches(items);
            setSelected((current) => current || items[0]?.id || '');
            setError(null);
        } catch (err) {
            setError(errorMessage(err, 'Failed to load batches'));
        }
    }, []);

    const refreshDetail = useCallback(async () => {
        if (!selected) {
            setDetail(null);
            return;
        }
        try {
            setDetail(await getReplayBatch(selected));
        } catch (err) {
            setError(errorMessage(err, 'Failed to load the batch'));
        }
    }, [selected]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        setDetail(null);
        refreshDetail();
    }, [refreshDetail]);

    const running = (detail?.summary.running ?? 0) > 0;
    useEffect(() => {
        if (!running) return undefined;
        const timer = setTimeout(() => {
            refreshDetail();
            refresh();
        }, POLL_MS);
        return () => clearTimeout(timer);
    }, [running, detail, refreshDetail, refresh]);

    const start = async () => {
        setStarting(true);
        setError(null);
        setNotice(null);
        try {
            const started = await startReplayBatch({
                feedback,
                userId: scope === 'user' ? userId : undefined,
                limit,
                model,
                promptSource,
            });
            if (started.skipped > 0) {
                setNotice(`${started.skipped} replies skipped: no recorded prompt (try the current prompts).`);
            }
            setSelected(started.batch_id);
            await refresh();
        } catch (err) {
            setError(errorMessage(err, 'Failed to start the batch'));
        } finally {
            setStarting(false);
        }
    };

    const summary = detail?.summary;

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle>Replay a batch</DialogTitle>
            <DialogContent dividers>
                <GlowAlert severity="info" sx={{ mb: 2 }}>
                    Runs one reply after another with the model you pick and compares each with what
                    happened. Nothing is changed: tools that write are replaced by stubs.
                </GlowAlert>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'flex-start' }}>
                    <TextField select size="small" label="Replies" value={feedback} sx={{ minWidth: 130 }}
                        onChange={(e) => setFeedback(e.target.value as Feedback)}>
                        <MenuItem value="down">👎 Thumbs down</MenuItem>
                        <MenuItem value="up">👍 Thumbs up</MenuItem>
                    </TextField>
                    <TextField select size="small" label="From" value={scope} sx={{ minWidth: 170 }}
                        onChange={(e) => setScope(e.target.value as 'user' | 'all')}>
                        {userId && <MenuItem value="user">{userName || 'This user'}</MenuItem>}
                        <MenuItem value="all">All users</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Most recent" value={limit} sx={{ minWidth: 110 }}
                        onChange={(e) => setLimit(Number(e.target.value))}>
                        {[5, 10, 25].map((n) => (
                            <MenuItem key={n} value={n}>{n}</MenuItem>
                        ))}
                    </TextField>
                    <Autocomplete
                        freeSolo
                        size="small"
                        options={REPLAY_MODEL_SUGGESTIONS}
                        inputValue={model}
                        onInputChange={(_, value) => setModel(value)}
                        sx={{ minWidth: 300 }}
                        renderInput={(params) => (
                            <TextField {...params} label="Model" helperText="Empty: the agent's own model" />
                        )}
                    />
                    <TextField select size="small" label="Prompts" value={promptSource} sx={{ minWidth: 150 }}
                        onChange={(e) => setPromptSource(e.target.value as PromptSource)}>
                        <MenuItem value="recorded">Recorded</MenuItem>
                        <MenuItem value="current">Current</MenuItem>
                    </TextField>
                    <LoadingButton variant="contained" onClick={start} loading={starting || running}>
                        {running ? 'Running...' : 'Replay batch'}
                    </LoadingButton>
                </Box>

                {error && <GlowAlert sx={{ mb: 2 }}>{error}</GlowAlert>}
                {notice && <Alert severity="warning" sx={{ mb: 2 }}>{notice}</Alert>}
                {batches?.length === 0 && <Typography color="text.secondary">No batches yet.</Typography>}

                {batches && batches.length > 0 && (
                    <TextField select size="small" fullWidth label="Batch" value={selected} sx={{ mb: 2 }}
                        onChange={(e) => setSelected(e.target.value)}>
                        {batches.map((batch) => (
                            <MenuItem key={batch.id} value={batch.id}>
                                {new Date(batch.created_at).toLocaleString()} · {describe(batch)}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                {summary && detail && (
                    <>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }} data-testid="batch-summary">
                            <Chip label={`${summary.done + summary.failed}/${summary.total} finished`} />
                            {summary.failed > 0 && <Chip color="error" label={`${summary.failed} failed`} />}
                            <Chip variant="outlined" label={`${summary.reply_changed} replies changed`} />
                            <Chip variant="outlined" color="warning" label={`${summary.tried_to_write} tried to write`} />
                            <Chip variant="outlined" color="info" label={`${summary.guard_fired} claim guard fired`} />
                            <Chip variant="outlined" color="warning" label={`${summary.checks_failed} failed a check`} />
                            <Chip
                                variant="outlined"
                                color="success"
                                label={`${summary.verdicts.better} better · ${summary.verdicts.same} same · ${summary.verdicts.worse} worse · ${summary.verdicts.unjudged} unjudged`}
                            />
                            {summary.avg_duration_ms !== null && (
                                <Chip variant="outlined" label={`avg ${(summary.avg_duration_ms / 1000).toFixed(1)}s`} />
                            )}
                            {detail.skipped > 0 && <Chip variant="outlined" label={`${detail.skipped} skipped`} />}
                        </Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Asked</TableCell>
                                    <TableCell>What happened</TableCell>
                                    <TableCell>Replay ({detail.model})</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {detail.runs.map((run) => (
                                    <Row
                                        key={run.id}
                                        run={run}
                                        onOpen={() => setOpenTurn(run.user_message_id)}
                                        onVerdict={refreshDetail}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
                {openTurn && <ReplayDialog messageId={openTurn} onClose={() => setOpenTurn(null)} />}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
